import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsPlus, BsTrash, BsArrowUp, BsArrowDown } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import TextInput from '#globalComponents/Forms/TextInput';
import { useErrorStore } from '#lib/stores/errorStore';

import { useCreatePipeline } from '../../../models/pipelines/mutations';

/**
 * Manual pipeline creation form — name, description and an editable list of
 * stages (add / remove / reorder). Used both inside NewPipelineModal (settings)
 * and inside the OnboardingModal manual phase, so it stays free of any modal
 * wrapper and lets the parent decide what happens after creation.
 */

const DEFAULT_STAGES = [
	{ name: 'Qualifizierung', stageType: 'active' },
	{ name: 'Kontaktaufnahme', stageType: 'active' },
	{ name: 'Angebot', stageType: 'active' },
	{ name: 'Gewonnen', stageType: 'won' },
	{ name: 'Verloren', stageType: 'lost' },
];

const ManualPipelineForm = ({ onCreated, onBack, submitLabel = 'Pipeline erstellen' }) => {
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [stages, setStages] = useState(DEFAULT_STAGES.map((s, i) => ({ ...s, stageOrder: i })));
	const [newStageName, setNewStageName] = useState('');

	const createPipeline = useCreatePipeline();
	const showError = useErrorStore((s) => s.showError);

	const handleAddStage = () => {
		if (!newStageName.trim()) return;
		setStages((prev) => [
			...prev,
			{
				name: newStageName.trim(),
				stageType: 'active',
				stageOrder: prev.length,
			},
		]);
		setNewStageName('');
	};

	const handleRemoveStage = (idx) => {
		setStages((prev) => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, stageOrder: i })));
	};

	const handleMoveStage = (idx, dir) => {
		setStages((prev) => {
			const next = [...prev];
			const target = idx + dir;
			if (target < 0 || target >= next.length) return prev;
			[next[idx], next[target]] = [next[target], next[idx]];
			return next.map((s, i) => ({ ...s, stageOrder: i }));
		});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!name.trim() || stages.length === 0) return;

		try {
			const created = await createPipeline.mutateAsync({
				name: name.trim(),
				description: description.trim() || null,
				stages: stages.map((s) => ({
					name: s.name,
					stageOrder: s.stageOrder,
					stageType: s.stageType,
				})),
			});
			onCreated(created);
		} catch (err) {
			showError({ error: err, title: 'Pipeline konnte nicht erstellt werden' });
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			<div className="flex items-center justify-between">
				<h2 className="text-lg font-bold text-text">Pipeline erstellen</h2>
				{onBack && (
					<button type="button" onClick={onBack} className="text-xs text-bgBase-800 hover:text-text">
						← Zurück
					</button>
				)}
			</div>

			<div>
				<label className="block text-xs font-medium text-text mb-1">Name *</label>
				<TextInput
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder="z. B. B2B SaaS Vertrieb"
					required
					autoFocus
				/>
			</div>

			<div>
				<label className="block text-xs font-medium text-text mb-1">Beschreibung</label>
				<TextInput
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					placeholder="Optional"
				/>
			</div>

			<div>
				<label className="block text-xs font-medium text-text mb-2">Stages ({stages.length})</label>
				<div className="space-y-1.5 mb-3">
					{stages.map((s, idx) => (
						<div
							key={idx}
							className="flex items-center gap-2 rounded-card border border-bgBase-500 bg-white px-3 py-1.5"
						>
							<span className="w-3 h-3 rounded-full flex-shrink-0 bg-primary-500" />
							<input
								type="text"
								value={s.name}
								onChange={(e) =>
									setStages((prev) =>
										prev.map((st, i) => (i === idx ? { ...st, name: e.target.value } : st)),
									)
								}
								className="text-sm text-text flex-1 min-w-0 bg-transparent border-0 outline-none focus:ring-0 p-0"
							/>
							<button
								type="button"
								onClick={() => handleMoveStage(idx, -1)}
								disabled={idx === 0}
								className="text-bgBase-800 hover:text-text disabled:opacity-30"
							>
								<BsArrowUp className="w-3 h-3" />
							</button>
							<button
								type="button"
								onClick={() => handleMoveStage(idx, 1)}
								disabled={idx === stages.length - 1}
								className="text-bgBase-800 hover:text-text disabled:opacity-30"
							>
								<BsArrowDown className="w-3 h-3" />
							</button>
							<button
								type="button"
								onClick={() => handleRemoveStage(idx)}
								className="text-warning-500 hover:text-warning-700"
							>
								<BsTrash className="w-3 h-3" />
							</button>
						</div>
					))}
				</div>

				<div className="flex gap-2">
					<TextInput
						value={newStageName}
						onChange={(e) => setNewStageName(e.target.value)}
						onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddStage())}
						placeholder="Neue Stage hinzufügen …"
						className="flex-1"
					/>
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={handleAddStage}
						disabled={!newStageName.trim()}
					>
						<BsPlus className="w-4 h-4" />
					</Button>
				</div>
			</div>

			<div className="flex justify-end gap-2 pt-2">
				{onBack && (
					<Button type="button" variant="outline" size="sm" onClick={onBack}>
						Abbrechen
					</Button>
				)}
				<Button
					type="submit"
					variant="solid"
					size="sm"
					loading={createPipeline.isPending}
					disabled={!name.trim() || stages.length === 0}
				>
					{submitLabel}
				</Button>
			</div>
		</form>
	);
};

ManualPipelineForm.propTypes = {
	onCreated: PropTypes.func.isRequired,
	onBack: PropTypes.func,
	submitLabel: PropTypes.string,
};

export default ManualPipelineForm;
